import { create } from 'zustand';
import type { Mode, ModeState } from './types';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';

/**
 * Mode store - Live vs Demo (HC1).
 *
 * Default is `demo` per master prompt v3.3 §HC1: "Demo Mode is the safe,
 * recorded fallback; Live Mode is opt-in".
 *
 * Phase 1 quant target: toggle latency under 100ms. The store is in-memory
 * and synchronous so toggling is effectively zero-cost; the latency budget
 * lives in the components that re-render.
 *
 * PROD-573 wires the `mode_switch` telemetry event. Both `setMode` (when
 * the value actually changes) and `toggle` emit `mode_switch` with payload
 * `{ from_mode, to_mode, latency_ms, correlation_id }`. Latency is a real
 * `performance.now()` delta. A no-op `setMode(current)` does NOT emit so
 * idempotent calls do not pollute the stream.
 */
export const useModeStore = create<ModeState>((set, get) => ({
  mode: 'demo' as Mode,
  setMode: (mode) => {
    const previous = get().mode;
    if (previous === mode) return;
    const start = performance.now();
    set({ mode });
    const latency = Math.round(performance.now() - start);
    emitTelemetry('mode_switch', {
      from_mode: previous,
      to_mode: mode,
      latency_ms: latency,
      correlation_id: newCorrelationId(),
    });
  },
  toggle: () => {
    const previous = get().mode;
    const next: Mode = previous === 'demo' ? 'live' : 'demo';
    const start = performance.now();
    set({ mode: next });
    const latency = Math.round(performance.now() - start);
    emitTelemetry('mode_switch', {
      from_mode: previous,
      to_mode: next,
      latency_ms: latency,
      correlation_id: newCorrelationId(),
    });
  },
}));
